import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useQuery } from '@apollo/client/react';
import { c, msgid } from 'ttag';

import {
    Badge,
    Button,
    Input,
    RegionState,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCellProps,
    TableRow,
} from '@freelancelabs/components';
import { EyeOutline, MagnifyingGlass } from '@freelancelabs/icons';

import ListCard, { WRAPPING_HEADER } from '../../components/ListCard';
import RefusalNotice from '../../components/RefusalNotice';
import { PartnerListsDocument } from '../../graphql/__generated__/types';
import DeclarePartnerListDialog from './DeclarePartnerListDialog';
import { partnerListRefusalOf } from './refusals';

const SEARCH_DEBOUNCE_MS = 300;

/**
 * « Listes de partenaires »: PCG-79 for the screen, PCG-276 for what it finally reads.
 *
 * **Why a screen of its own and not a panel on an account.** A partner list is a platform
 * referential: it carries no `organization_id`, because its content isolates nothing —
 * « Partenaires IA » means the same thing for every client. Hanging it off an account
 * would state that the list belongs to that account, which is the governance error the
 * API warns about. « Rôles » is the precedent.
 *
 * **It reads `partnerLists`, and until PCG-276 it read `assignablePartnerLists`, which was
 * the wrong query.** That one is the *attribution* view: bounded on `manage_users`, live
 * lists only, and carrying neither the display mode nor the number of holders. The
 * consequences were visible on this screen: no « Anonymisation » column, no « Utilisateurs
 * associés », and no way to reach the composition of a list. The gap was named in this
 * file's own header; it is closed.
 *
 * **The read and the write are still bounded by different rights**, and nothing here hides
 * that: this query takes `manage_referential`, like every gesture below it, so an operator
 * who can see the screen can act on it. The asymmetry that remains is on
 * `assignablePartnerLists`, which the user sheet uses on `manage_users` — an open business
 * arbitration on the API side, not something this screen can settle.
 *
 * **Neither pagination nor sorting**, as the mockup: the catalogue is small by nature, a
 * theme being shared by every client. Archived lists are not rendered, and their name
 * stays reserved for ever: the refusal says so, and offers no gesture, because there is no
 * door to an archived list.
 */
const PartnerListsPage = () => {
    const [declaring, setDeclaring] = useState(false);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [search]);

    const { data, loading, error } = useQuery(PartnerListsDocument, {
        variables: { search: debouncedSearch || null },
        // The four facts of a row change from the sheet, so from another screen: coming
        // back through the router would otherwise show the counters as they were.
        fetchPolicy: 'cache-and-network',
    });

    const lists = data?.partnerLists ?? [];
    const filtered = Boolean(debouncedSearch);
    // On the absence of data, never on `loading`: otherwise every refetch empties a table
    // being read, and every keystroke makes « no list » flash.
    const showSkeleton = loading && !data;

    const columns: TableHeaderCellProps[] = (
        [
            { id: 'name', label: c('Partner lists').t`List name`, minWidth: 240 },
            { id: 'partners', label: c('Partner lists').t`Partners`, minWidth: 140 },
            { id: 'holders', label: c('Partner lists').t`Attached users`, minWidth: 170 },
            { id: 'anonymisation', label: c('Partner lists').t`Anonymisation`, minWidth: 130 },
            { id: 'detail', label: c('Partner lists').t`Detail`, minWidth: 150, textAlign: 'end' },
        ] as TableHeaderCellProps[]
    ).map((column) => ({ ...column, className: WRAPPING_HEADER }));

    return (
        <div className="flex flex-col gap-5">
            <header className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="text-xl font-semibold tracking-tight">{c('Partner lists').t`Partner lists`}</h1>
                    <p className="mt-1 max-w-2xl text-xs text-text-600">
                        {c('Partner lists')
                            .t`What a portal user sees of the partner network. A list is a named selection of eligible partners, anonymous or nominative, and its name is a theme shared by every client.`}
                    </p>
                </div>
                <Button onClick={() => setDeclaring(true)}>{c('Partner lists').t`Declare a list`}</Button>
            </header>

            <ListCard
                label={c('Partner lists').t`Partner lists`}
                toolbar={
                    <Input
                        value={search}
                        onValue={setSearch}
                        clearable
                        prefix={<MagnifyingGlass size={16} />}
                        placeholder={c('Partner lists').t`List name`}
                        aria-label={c('Partner lists').t`Search a list by name`}
                        className="max-w-sm"
                    />
                }
            >
                <Table>
                    <TableHeader resizable cells={columns} />
                    <TableBody loading={showSkeleton} loadingRows={4} loadingColumns={columns.length}>
                        {lists.map((list) => (
                            <TableRow key={list.id}>
                                <TableCell className="font-medium">{list.name}</TableCell>
                                <TableCell>
                                    {c('Partner lists').ngettext(
                                        msgid`${list.partnerCount} partner`,
                                        `${list.partnerCount} partners`,
                                        list.partnerCount
                                    )}
                                </TableCell>
                                <TableCell>
                                    {/* Zero is written, never left blank: an empty cell in a
                                        column of counts reads as a broken row. */}
                                    {list.holderCount > 0 ? (
                                        c('Partner lists').ngettext(
                                            msgid`${list.holderCount} user`,
                                            `${list.holderCount} users`,
                                            list.holderCount
                                        )
                                    ) : (
                                        <span className="text-text-600">{c('Partner lists').t`No user`}</span>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {/* Always shown, never empty: the mode decides what the
                                        client reads, so its absence would be the important
                                        half of the row missing. */}
                                    <Badge size="small" category={list.isAnonymous ? 'info' : 'default'}>
                                        {list.isAnonymous
                                            ? c('Partner lists').t`Anonymous`
                                            : c('Partner lists').t`Nominative`}
                                    </Badge>
                                </TableCell>
                                <TableCell className="text-end">
                                    <Link to={`/listes-de-partenaires/${list.id}`}>
                                        <Button
                                            category="weak"
                                            size="small"
                                            icon
                                            aria-label={c('Partner lists').t`View the list`}
                                            title={c('Partner lists').t`View the list`}
                                        >
                                            <EyeOutline size={16} />
                                        </Button>
                                    </Link>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                {/* Three states and not one, and the middle one is why the search wording
                    matters: it runs on the NAME OF THE LIST, never on its partners. */}
                {!showSkeleton && error && (
                    <div className="px-4 py-6">
                        <RefusalNotice refusal={partnerListRefusalOf(error)} />
                    </div>
                )}
                {!showSkeleton && !error && lists.length === 0 && filtered && (
                    <RegionState
                        title={c('Partner lists').t`No list matches this search`}
                        description={c('Partner lists')
                            .t`The search runs on the name of the list, never on the partners it holds nor on the users attached to it. Archived lists are not shown either, and they keep their name reserved.`}
                    >
                        <Button category="weak" size="small" onClick={() => setSearch('')}>
                            {c('Partner lists').t`Clear the search`}
                        </Button>
                    </RegionState>
                )}
                {!showSkeleton && !error && lists.length === 0 && !filtered && (
                    <RegionState
                        title={c('Partner lists').t`No partner list exists yet`}
                        description={c('Partner lists')
                            .t`Declare one: its name is a theme, and it never names a client. Without a list, the Partners module stays hidden for a portal user even when the right is granted.`}
                    >
                        <Button size="small" onClick={() => setDeclaring(true)}>
                            {c('Partner lists').t`Declare a list`}
                        </Button>
                    </RegionState>
                )}
            </ListCard>

            {declaring && <DeclarePartnerListDialog onClose={() => setDeclaring(false)} />}
        </div>
    );
};

export default PartnerListsPage;
